import React, { useEffect, useRef } from "react";
import {
  Stack,
  Divider,
  Input,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  InputRightElement,
  InputGroup,
  AbsoluteCenter,
  Box,
} from "@chakra-ui/react";
import ParrotText from "@/components/ParrotText";
import { useToast } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import {
  callWithAuth,
  putTempUserToFullUser,
  axiosInstance,
} from "../api/Api.ts";
import {
  PutTempUserToFullUserRequest,
  GetUserInfoResponse,
} from "@shared_types";
import { tokenStorage } from "../api/tokenStorage.tsx";
import { signUpUser } from "../api/login.tsx";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuthProviders } from "@/hooks/UseAuthProviders.tsx";
import { OAUTH_REDIRECT_URL } from "@/api/base";
import { getPlatform } from "@/lib/iap";
import { SocialAuthButtons } from "@/components/SocialAuthButtons.tsx";
import { LoadingPage } from "./LoadingPage";
import { usePostHog } from "posthog-js/react";
const IS_APPLE = getPlatform() === "apple";

const SignUpPage: React.FC = () => {
  const posthog = usePostHog();
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [show, setShow] = React.useState(false);
  const redirectUrl = OAUTH_REDIRECT_URL + "/redirect";
  const isTokenSet = tokenStorage.isTokenSet();
  const { isPending, data } = useQuery({
    queryKey: ["UserInfoQuerySignUp"],
    retry: 1,
    queryFn: async () => {
      const authToken = tokenStorage.getToken();
      const response = await axiosInstance.get<GetUserInfoResponse>(
        "/login/status",
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      return response.data;
    },
  });
  const runOnce = useRef(false);

  useEffect(() => {
    if (runOnce.current) return;
    runOnce.current = true;
    posthog.capture("sign_up_page_viewed");
  }, []);

  // if the id is 0 that means we are logged out, hence undefined data is
  // preventing redirect
  let userInfo: GetUserInfoResponse | undefined = data;
  if (data && data.id == 0) {
    userInfo = undefined;
  }
  const authProviders = useAuthProviders();

  const nav = useNavigate();
  const toast = useToast();

  const handleCreateAccount = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const displayName = name;

    if (displayName.trim() === "") {
      toast({
        title: "Name required",
        description: "Please enter your name",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } else if (email.trim() === "") {
      toast({
        title: "Email required",
        description: "Please enter an email address",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } else if (!emailRegex.test(email)) {
      toast({
        title: "Invalid email format",
        description: "Please enter a valid email address",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } else if (password.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters long",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } else {
      try {
        // TODO make sure only istemptrial and istemppremium can do this, otherwise handle their error.
        if (userInfo) {
          await signUpUser(email, password, displayName);
          const data: PutTempUserToFullUserRequest = {
            parrotUserId: userInfo.id,
          };
          await callWithAuth(putTempUserToFullUser, data);
          nav("/placement");
          return;
        }
      } catch (err: any) {
        let uiErrMsg = pocketBaseUiErrorMesg(err);
        if (!uiErrMsg) {
          uiErrMsg = "Sign-up failed please try again.";
        }
        toast({
          title: "Sign-up failed",
          description: `${uiErrMsg}`,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
    }
  };

  const pocketBaseUiErrorMesg = (err: any) => {
    const nameProblem = err.response?.data?.name;
    const emailProblem = err.response?.data?.email;
    const passProblem = err.response?.data?.password;
    if (emailProblem) {
      return emailProblem.message;
    }
    if (nameProblem) {
      return nameProblem.message;
    }
    if (passProblem) {
      return passProblem.message;
    }
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handlePassswordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPassword(event.target.value);
  };

  if (isTokenSet && isPending) return <LoadingPage />;

  // redirect to home
  // full user accidently end up here
  if (
    userInfo &&
    userInfo.isTemp == false &&
    userInfo.isTempTrial == false &&
    userInfo.isTempPremium == false
  ) {
    return <Navigate to="/home/learn" />;
  }

  // if they are temp that means the upgrade wasnt successful.
  // This is a horrrible horrible sitation, unfortantly
  // not recoverable. We can send them back to the start
  // and they will have to start over hopefully they didnt actually pay.
  if (userInfo && userInfo.isTemp === true) {
    return <Navigate to="/" />;
  }

  // no user info? what are we doing here...
  // this is bad if this happens. We should not have a
  // user without any user information at this page.
  // all progress forward requires the existing user id.
  if (!userInfo) {
    return <Navigate to="/" />;
  }

  return (
    <Stack
      width={"100%"}
      height={"100%"}
      justify={"center"}
      align={"center"}
      justifyContent={"space-between"}
      overflow={"hidden"}
      className="appPadding overflow-hidden"
    >
      <div className="self-start h-8"></div>
      <Stack align="center" spacing={4} width={"100%"}>
        <ParrotText className="-mt-4" />
        <Stack align="center" spacing={0} paddingBottom={2}>
          <div className="text-black text-center text-xl font-semibold my-1">
            Create your account!
          </div>
          <div className="text-sm">
            Time to create your account and start your journey to fluency.
          </div>
        </Stack>
      </Stack>

      <Stack
        align="center"
        spacing={4}
        width={"100%"}
        bg={"white"}
        padding={4}
        rounded={"2xl"}
      >
        <Stack spacing={2} width={"100%"}>
          <FormControl isRequired>
            <FormLabel fontSize={"sm"} marginBottom={1}>
              First Name
            </FormLabel>
            <Input value={name} isRequired onChange={handleNameChange} />
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize={"sm"} marginBottom={1}>
              Email
            </FormLabel>
            <Input value={email} isRequired onChange={handleEmailChange} />
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize={"sm"} marginBottom={1}>
              Password
            </FormLabel>
            <InputGroup size="md">
              <Input
                value={password}
                type={show ? "text" : "password"}
                isRequired
                onChange={handlePassswordChange}
              />
              <InputRightElement width="4.5rem">
                <Button size="sm" onClick={() => setShow(!show)}>
                  {show ? "Hide" : "Show"}
                </Button>
              </InputRightElement>
            </InputGroup>
            <FormHelperText textAlign={"start"}>
              {" "}
              At least 8 characters long
            </FormHelperText>
          </FormControl>
        </Stack>

        <Stack width={"100%"} gap={1}>
          <Button
            type="button"
            background={"var(--color-blue)"}
            textColor={"white"}
            width={"100%"}
            onClick={handleCreateAccount}
          >
            Create account
          </Button>
        </Stack>

        <Box width={"100%"} position={"relative"}>
          <Divider />
          <AbsoluteCenter
            bg="white"
            px="4"
            textColor={"gray.500"}
            fontSize={"sm"}
          >
            OR
          </AbsoluteCenter>
        </Box>
        <SocialAuthButtons
          authProviders={authProviders}
          redirectUrl={redirectUrl}
          isApplePlatform={IS_APPLE}
          userID={userInfo.id}
          isTemp={
            userInfo.isTemp || userInfo.isTempTrial || userInfo.isTempPremium
          }
        />
      </Stack>
      <div className="w-full min-h-10"></div>
    </Stack>
  );
};

export default SignUpPage;
